import { useMemo, useState } from 'react';

import { analysisFor, formatDb, formatHz } from './analysis.js';
import { ftom, isNum, isObj, midiToNote } from './param-values.js';
import './sound-editors.css';

function Stat({ label, value, sub, tone, title }: { label: string; value: string; sub?: string; tone?: 'ok' | 'warn' | 'bad'; title?: string }) {
  return (
    <div className={`snd-stat${tone ? ` is-${tone}` : ''}`} title={title}>
      <span className="snd-stat-label">{label}</span>
      <span className="snd-stat-value mono">{value}</span>
      {sub && <span className="snd-stat-sub">{sub}</span>}
    </div>
  );
}

/**
 * Analysis numbers for a render: duration, integrated loudness (vs. the
 * document's `master.loudness` target), true/sample peak, RMS, loudness
 * range, pitch, spectral centroid, onsets, DC offset and clipping, plus the
 * renderer's warnings. Uses the engine analysis object when given and
 * measures the buffer locally (BS.1770-4) for anything missing.
 */
export function AnalysisPanel({
  analysis,
  left,
  right,
  rate = 48000,
  warnings = [],
  doc,
  onSeek,
}: {
  analysis?: unknown;
  left?: Float32Array | null;
  right?: Float32Array | null;
  rate?: number;
  warnings?: string[];
  doc?: unknown;
  onSeek?: (t: number) => void;
}) {
  const a = useMemo(() => analysisFor(analysis, left ?? undefined, right ?? undefined, rate), [analysis, left, right, rate]);
  const [showExtra, setShowExtra] = useState(false);
  const target = isObj(doc) && isObj(doc.master) && isNum(doc.master.loudness) ? doc.master.loudness : undefined;
  const limiter = !(isObj(doc) && isObj(doc.master) && doc.master.limiter === false);

  if (!a) return <div className="snd-analysis is-empty">Render the sound to measure it.</div>;

  const lufsTone = a.lufs === undefined || target === undefined || !Number.isFinite(a.lufs) ? undefined : Math.abs(a.lufs - target) <= 1 ? 'ok' : 'warn';
  const tpTone = a.truePeakDb === undefined ? undefined : a.truePeakDb > 0 ? 'bad' : a.truePeakDb > -1 && limiter ? 'warn' : 'ok';
  const extra = Object.entries(a.extra);

  return (
    <div className="snd-analysis">
      <div className="snd-stats">
        <Stat label="Duration" value={a.duration !== undefined ? `${a.duration.toFixed(3)} s` : '—'} />
        <Stat
          label="Loudness"
          value={formatDb(a.lufs, 'LUFS')}
          sub={target !== undefined ? `target ${target} LUFS` : 'integrated (BS.1770-4)'}
          tone={lufsTone}
        />
        <Stat label="True peak" value={formatDb(a.truePeakDb, 'dBTP')} sub={a.source === 'local' ? '≈ 4× interpolated' : undefined} tone={tpTone} />
        <Stat label="Sample peak" value={formatDb(a.peakDb, 'dBFS')} />
        <Stat label="RMS" value={formatDb(a.rmsDb, 'dBFS')} />
        {a.shortTermMaxLufs !== undefined && <Stat label="Short-term max" value={formatDb(a.shortTermMaxLufs, 'LUFS')} />}
        {a.lra !== undefined && <Stat label="Loudness range" value={`${a.lra.toFixed(1)} LU`} />}
        <Stat
          label="Pitch"
          value={formatHz(a.pitchHz)}
          sub={a.pitchHz ? `${midiToNote(ftom(a.pitchHz))} (${ftom(a.pitchHz).toFixed(2)})` : a.source === 'local' ? 'engine analysis only' : 'unpitched'}
        />
        <Stat label="Centroid" value={formatHz(a.centroidHz)} sub="brightness" />
        <Stat label="Onsets" value={String(a.onsets.length)} />
        {a.dcOffset !== undefined && <Stat label="DC offset" value={a.dcOffset.toFixed(5)} tone={Math.abs(a.dcOffset) > 0.01 ? 'warn' : undefined} />}
        {a.clipped !== undefined && <Stat label="Clipped" value={String(a.clipped)} sub="samples ≥ 0 dBFS" tone={a.clipped > 0 ? 'bad' : undefined} />}
      </div>
      {a.onsets.length > 0 && (
        <div className="snd-chips">
          {a.onsets.slice(0, 64).map((t, i) => (
            <button key={i} type="button" className="snd-chip is-button mono" title="Seek" onClick={() => onSeek?.(t)}>
              {`${t.toFixed(3)}s`}
            </button>
          ))}
          {a.onsets.length > 64 && <span className="snd-hint">{`+${a.onsets.length - 64} more`}</span>}
        </div>
      )}
      {warnings.length > 0 && (
        <ul className="snd-warnings">
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
      <div className="snd-row">
        <span className="snd-hint">{a.source === 'engine' ? 'Engine analysis' : 'Measured in the editor (engine analysis unavailable)'}</span>
        {extra.length > 0 && (
          <button type="button" className="snd-link" onClick={() => setShowExtra((s) => !s)}>
            {showExtra ? 'hide details' : `${extra.length} more measurements`}
          </button>
        )}
      </div>
      {showExtra && (
        <table className="snd-extra">
          <tbody>
            {extra.map(([k, v]) => (
              <tr key={k}>
                <td>{k}</td>
                <td className="mono">{Math.abs(v) >= 1000 || Number.isInteger(v) ? String(Math.round(v * 1000) / 1000) : v.toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
